import { Dietary } from '@hungryroot/constants';
import {
  type Quiz,
  Goals,
  HouseHoldSizeEnum,
  EatingHabit,
  FoodPrep,
  ProteinTagTernaryRating,
  type ProteinTagValue,
  type ProteinTagTernaryValue,
} from '@hungryroot/funnel';
import { ContainerFrequencyOptions } from '@hungryroot/rest';
import { tags } from '@hungryroot/tags';

function pickRandom<T>(arr: T[], min: number, max: number): T[] {
  const count = randomInt(min, Math.min(max, arr.length));
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

function pickOne<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomRating(): -1 | 0 | 1 {
  return pickOne([-1, 0, 1]) as -1 | 0 | 1;
}

function toProteinTagValues(ids: number[]): ProteinTagValue[] {
  return ids.map((id) => ({ id, liked: randomRating() }));
}

function toTernaryTagValues(ids: number[]): ProteinTagTernaryValue[] {
  return ids.map((id) => ({
    id,
    rated: pickOne([
      ProteinTagTernaryRating.NoThanks,
      ProteinTagTernaryRating.SoSo,
      ProteinTagTernaryRating.Absolutely,
    ]),
  }));
}

const MEAT_TAG_IDS = [
  tags.BeefAndBison,
  tags.Poultry,
  tags.Pork,
  tags.Lamb,
  tags.Bison,
  tags.Steak,
  tags.GroundBeef,
  tags.Turkey,
  tags.Chicken,
];
const SEAFOOD_TAG_IDS = [
  tags.FreshFish,
  tags.Shellfish,
  tags.SmokedFish,
  tags.Tuna,
  tags.Trout,
  tags.Whitefish,
  tags.Salmon,
];
const PLANT_TAG_IDS = [
  tags.TofuAndTempeh,
  tags.BeansAndLentils,
  tags.MeatAlternatives,
  tags.DairyFreeCheese,
];
const CUISINE_TAG_IDS = [
  tags.Indian,
  tags.Mediterranean,
  tags.Mexican,
  tags.American,
  tags.Italian,
  tags.Spicy,
  tags.Thai,
  tags.Chinese,
  tags.Japanese,
  tags.Korean,
  tags.Fusion,
];
const FLAVOR_TAG_IDS = [
  tags.Creamy,
  tags.Cheesy,
  tags.Herbs,
  tags.Umami,
  tags.Sweet,
  tags.Spiced,
  tags.Smoky,
  tags.Garlicky,
  tags.Tangy,
  tags.Peppery,
  tags.Hearty,
];
const MACRO_NUTRIENT_TAG_IDS = [
  tags.CarbConscious,
  tags.HighProtein,
  tags.LessSugar,
  tags.LowSodium,
  tags.LowCholesterol,
  tags.LowFodmap,
  tags.CalorieConscious,
  tags.HighFiber,
  tags.LowFat,
];
const HEALTH_GOAL_TAG_IDS = [
  tags.LoseWeight,
  tags.LowerBloodPressure,
  tags.ManageBloodSugar,
  tags.SupportGutHealth,
  tags.ReduceInflammation,
  tags.SupportGLP1,
  tags.IncreaseEnergy,
  tags.StrengthenImmunity,
  tags.SupportJointAndMuscleRecovery,
  tags.SupportThyroidHealth,
  tags.PerimenopauseOrMenopause,
  tags.SupportPregnancyOrPostpartum,
  tags.LowerCholesterol,
  tags.IncreaseStrength,
];
const DISLIKE_TAG_IDS = [
  tags.Onions,
  tags.Garlic,
  tags.Mushrooms,
  tags.Cilantro,
  tags.BellPeppers,
  tags.Tomatoes,
  tags.Eggplants,
  tags.Potatoes,
  tags.Beets,
  tags.Coconut,
  tags.BrusselSprouts,
  tags.ChiliPeppers,
  tags.SoySauce,
  tags.Miso,
  tags.MasalaSpices,
];

const LUNCH_TAG_IDS = [
  tags.LunchMainsAndSides,
  tags.LunchGrainBowls,
  tags.LunchPastas,
  tags.LunchTacos,
  tags.LunchStirFries,
  tags.LunchSalads,
  tags.LunchWraps,
  tags.LunchSandwiches,
  tags.LunchBurgers,
  tags.LunchPizzas,
  tags.LunchSoups,
];
const DINNER_TAG_IDS = [
  tags.DinnerMainsAndSides,
  tags.DinnerBowls,
  tags.DinnerPastas,
  tags.DinnerTacos,
  tags.DinnerStirFries,
  tags.DinnerKids,
  tags.DinnerSalads,
  tags.DinnerWraps,
  tags.DinnerSandwiches,
  tags.DinnerBurgers,
  tags.DinnerBakes,
  tags.DinnerPizzas,
  tags.DinnerSoupsAndStews,
];
const READY_MADE_TAG_IDS = [
  tags.Salads,
  tags.Sandwiches,
  tags.Pizza,
  tags.Bowls,
  tags.Soups,
];
const FOOD_FORMAT_TAG_IDS = [tags.PreCookedProteins, tags.PreCutProduce];

// Food quality tag IDs, some are diet-dependent
const CHEF_STYLE_RECIPES = 809;
const READY_TO_EAT = 810;

const ALL_GOALS = [
  Goals.SaveTime,
  Goals.ImproveHealth,
  Goals.LoseWeight,
  Goals.TryNewThings,
  Goals.GroceryShopLess,
  Goals.WasteLessFood,
  Goals.SaveMoney,
  Goals.SimplifyCooking,
  Goals.LowerStress,
];

const ALL_DIETARY = [
  Dietary.DairyFree,
  Dietary.GlutenFree,
  Dietary.SoyFree,
  Dietary.TreeNutFree,
  Dietary.PeanutFree,
  Dietary.EggFree,
  Dietary.ShellfishFree,
  Dietary.SesameFree,
  Dietary.GrainFree,
  Dietary.GarlicFree,
  Dietary.OnionFree,
  Dietary.CaffeineFree,
];

const ALL_FREQUENCIES = [
  ContainerFrequencyOptions.All,
  ContainerFrequencyOptions.Some,
  ContainerFrequencyOptions.None,
];

function getFoodQualitiesForHabit(habit: EatingHabit): number[] {
  const base = [
    CHEF_STYLE_RECIPES,
    READY_TO_EAT,
    tags.OrganicProduce,
    tags.QuickRecipes,
  ];

  if (habit === EatingHabit.Vegan || habit === EatingHabit.Vegetarian) {
    return base;
  }

  if (habit === EatingHabit.Pescatarian) {
    return [...base, tags.WildCaughtSeafood];
  }

  return [...base, tags.GrassFedMeat, tags.WildCaughtSeafood];
}

function getDislikeTagsForHabit(habit: EatingHabit): number[] {
  if (habit === EatingHabit.Vegan) {
    return DISLIKE_TAG_IDS;
  }
  return [...DISLIKE_TAG_IDS, tags.Paneer];
}

/**
 * Maps the dietary restriction ID from the create-test-customer form
 * to the corresponding EatingHabit enum value used by the funnel quiz.
 */
const DIETARY_RESTRICTION_TO_EATING_HABIT: Record<number, EatingHabit> = {
  11: EatingHabit.Omnivore,
  1: EatingHabit.Vegan,
  2: EatingHabit.Vegetarian,
  3: EatingHabit.Pescatarian,
  12: EatingHabit.Flexitarian,
};

export function generateRandomQuiz(dietaryRestrictionId: number): Quiz {
  const householdSize = pickOne([
    HouseHoldSizeEnum.ME,
    HouseHoldSizeEnum.ME_PLUS_ONE,
    HouseHoldSizeEnum.ME_PLUS_MORE,
  ]);

  const habits =
    DIETARY_RESTRICTION_TO_EATING_HABIT[dietaryRestrictionId] ??
    EatingHabit.Omnivore;
  const isVegan = habits === EatingHabit.Vegan;
  const isVeganOrVegetarian = isVegan || habits === EatingHabit.Vegetarian;
  const isPescatarian = habits === EatingHabit.Pescatarian;

  const meats =
    isVeganOrVegetarian || isPescatarian
      ? []
      : toTernaryTagValues(pickRandom(MEAT_TAG_IDS, 3, 6));

  const seafood = isVeganOrVegetarian
    ? []
    : toTernaryTagValues(pickRandom(SEAFOOD_TAG_IDS, 2, 5));

  const foodQualityOptions = getFoodQualitiesForHabit(habits);
  const dislikeOptions = getDislikeTagsForHabit(habits);

  // Filter dietary restrictions for compatibility with eating habit
  let dietaryOptions = ALL_DIETARY;
  if (isVeganOrVegetarian) {
    // Shellfish-free is redundant for vegans/vegetarians (they already don't eat it)
    dietaryOptions = dietaryOptions.filter((d) => d !== Dietary.ShellfishFree);
  }

  return {
    householdSize,
    adultsCount: householdSize === HouseHoldSizeEnum.ME ? 1 : randomInt(1, 3),
    childrenCount: randomInt(0, 2),
    youngChildrenCount: randomInt(0, 1),
    goals: pickRandom(ALL_GOALS, 2, 4),
    healthGoals: pickRandom(HEALTH_GOAL_TAG_IDS, 1, 3),
    budget: null,
    habits,
    dietary: pickRandom(dietaryOptions, 0, 2),
    macroNutrient: pickRandom(MACRO_NUTRIENT_TAG_IDS, 1, 3),
    foodQualities: pickRandom(foodQualityOptions, 1, 3),
    foodFormats: pickRandom(FOOD_FORMAT_TAG_IDS, 0, 2),
    foodPref: pickOne([FoodPrep.Simple, FoodPrep.ChoppingOrPrepping]),
    lunchPicks: [],
    dinnerPicks: [],
    swipeRatings: [],
    cuisines: toProteinTagValues(pickRandom(CUISINE_TAG_IDS, 4, 8)),
    flavors: toProteinTagValues(pickRandom(FLAVOR_TAG_IDS, 3, 6)),
    meats,
    seafood,
    plants: toTernaryTagValues(pickRandom(PLANT_TAG_IDS, 2, 4)),
    dislike: pickRandom(dislikeOptions, 1, 3),
    dislikedProductIds: null,
    likedProductIds: null,
    avoidancesHabitsSnapshot: null,
    breakfast: [],
    breakfastFrequency: pickOne(ALL_FREQUENCIES),
    lunchDinner: [],
    lunch: pickRandom(LUNCH_TAG_IDS, 3, 6),
    dinner: pickRandom(DINNER_TAG_IDS, 3, 6),
    lunchFrequency: pickOne(ALL_FREQUENCIES),
    dinnerFrequency: pickOne(ALL_FREQUENCIES),
    readyMade: pickRandom(READY_MADE_TAG_IDS, 1, 3),
    snacks: [],
    snacksFrequency: pickOne(ALL_FREQUENCIES),
    sweets: [],
    sweetsFrequency: pickOne(ALL_FREQUENCIES),
  };
}
